import { array, object, optional, string } from 'zod';
const MATCH_PAIR_SEPARATOR = ':';

const MatchItemIdSchema = string()
  .min(1, 'Match item id is required')
  .refine(id => !id.includes(MATCH_PAIR_SEPARATOR), {
    message: 'Match item id cannot contain the pair separator',
  });

const MatchItemSchema = object({
  id: MatchItemIdSchema,
  text: string().max(50, 'Match item text must be at most 50 characters'),
  richText: optional(string()),
  equation: optional(string()),
  imageUrl: optional(string()),
}).refine(
  item =>
    item.text.trim().length > 0 ||
    !!item.richText?.trim() ||
    !!item.equation?.trim() ||
    !!item.imageUrl?.trim(),
  {
    message: 'Match item text, equation or image is required',
  },
);

const AnswerPairSchema = object({
  leftId: MatchItemIdSchema,
  rightId: MatchItemIdSchema,
});

export const QuizMatchDataSchema = object({
  leftItems: array(MatchItemSchema).min(2).max(5),
  rightItems: array(MatchItemSchema).min(2).max(5),
  answerPairs: array(AnswerPairSchema).min(2).max(5),
}).refine(
  data => {
    if (
      data.leftItems.length !== data.rightItems.length ||
      data.leftItems.length !== data.answerPairs.length
    ) {
      return false;
    }

    const leftIds = new Set(data.leftItems.map(item => item.id));
    const rightIds = new Set(data.rightItems.map(item => item.id));
    const allItemIds = new Set([...leftIds, ...rightIds]);
    const pairedLeftIds = new Set(data.answerPairs.map(pair => pair.leftId));
    const pairedRightIds = new Set(data.answerPairs.map(pair => pair.rightId));

    return (
      leftIds.size === data.leftItems.length &&
      rightIds.size === data.rightItems.length &&
      allItemIds.size === data.leftItems.length + data.rightItems.length &&
      pairedLeftIds.size === leftIds.size &&
      pairedRightIds.size === rightIds.size &&
      data.answerPairs.every(
        pair => leftIds.has(pair.leftId) && rightIds.has(pair.rightId),
      )
    );
  },
  {
    message: 'Every match item must be paired exactly once',
    path: ['answerPairs'],
  },
);
